import { useState } from 'react'
import type { DragEvent } from 'react'
import cadPartRenderUrl from '../../../assets/parts/cad-part-render.png'
import { IconUpload } from '../../../components/icons'
import { ACCEPTED_CAD_EXTENSIONS } from '../types'

export interface DropzoneProps {
  readonly onFilesSelected: (files: readonly File[]) => void
  readonly disabled?: boolean
  /** Overrides for the Uploads-landing copy (UID-140 S2,
   * `Fictiv Platform.dc.html:2425-2426`), which reads differently than
   * Home's original "Drag and drop…" copy. Defaulted to Home's existing
   * copy so Home (and this component's own tests) are unaffected. */
  readonly title?: string
  readonly helperText?: string
  /** Skips this component's own `.card` wrapper so a caller (Uploads
   * landing, UID-140 S3) can compose it inside a larger card shared with the
   * "Uploading to" workspace-selector row below it, matching the design's
   * single bordered card spanning both (`Fictiv Platform.dc.html:2421`).
   * Home renders `Dropzone` standalone, so it keeps the default wrapper. */
  readonly bare?: boolean
}

/** `accept` attribute value + the helper copy below both derive from the
 * single `ACCEPTED_CAD_EXTENSIONS` list (code-review S1) — previously the
 * `<input>` had no `accept` filter at all and the helper text was a
 * separately hand-typed sentence, so the two (and the real allowlist) could
 * silently drift out of sync. Exported so other CAD-upload entry points
 * (e.g. `HomePage`'s "Choose files or BOM" trigger, a second `<input
 * type="file">` for this exact same `handleFilesSelected` seam) filter the
 * picker identically instead of re-deriving their own copy of this string. */
export const CAD_ACCEPT_ATTR = ACCEPTED_CAD_EXTENSIONS.join(',')
/* Home dropzone helper — verbatim design copy (`Fictiv Platform.dc.html`),
 * per product decision to match the mock exactly. NOTE: this hardcodes the
 * design's wording rather than deriving from ACCEPTED_CAD_EXTENSIONS, so it
 * can drift from the real allowlist, and "up to 200 files" is design copy not
 * a code-enforced limit. */
const HELPER_TEXT =
  'STEP, STP, IGES, SLDPRT, X_T, STL, PDF drawings, and CSV / BOM spreadsheets · up to 200 files · or click to browse'

/** Uploads-landing helper copy (UID-140 S2, `Fictiv Platform.dc.html:2426`):
 * matches the design's "we analyze geometry…" phrasing while still deriving
 * the format list from the single-source-of-truth `ACCEPTED_CAD_EXTENSIONS`
 * (not the design's hand-typed "STEP, STL, IGES · DXF, DWG, PDF" list, which
 * names DXF/DWG — formats the real allowlist doesn't accept — the allowlist
 * is the accuracy source of truth here, not the mock's copy). */
export const UPLOADS_HELPER_TEXT = `${ACCEPTED_CAD_EXTENSIONS.map((ext) => ext.slice(1).toUpperCase()).join(', ')} — we analyze geometry, cross-check drawings, and match the catalog automatically.`

/**
 * CAD/drawing dropzone — same visual pattern as Home's `.home-dropzone`
 * (screens.md §10: "same pattern as Home"), extended with the drag-over
 * highlight state screens.md calls out for the Uploads screen specifically
 * (Home's version doesn't implement it). A real `<label>`+hidden `<input
 * type="file">` so keyboard/click activation and screen-reader labeling
 * come for free; drag-and-drop is a pointer-only enhancement on top.
 *
 * QA BUG-1 (High, WCAG keyboard trap): the input is hidden with the
 * codebase's `.sr-only` class (visually hidden, still in the tab order),
 * NOT `style={{ display: 'none' }}` — `display:none` removes an element
 * from the accessibility tree and tab order entirely, so keyboard/SR users
 * could never reach it to open the file picker. `.sr-only` keeps it
 * focusable and label-associated while remaining visually hidden behind
 * the styled label.
 */
export function Dropzone({
  onFilesSelected,
  disabled = false,
  title = 'Drag and drop your CAD files and drawings',
  helperText = HELPER_TEXT,
  bare = false,
}: DropzoneProps) {
  const [dragOver, setDragOver] = useState(false)

  function handleDrop(event: DragEvent<HTMLLabelElement>) {
    event.preventDefault()
    setDragOver(false)
    if (disabled) return
    const files = [...event.dataTransfer.files]
    if (files.length > 0) onFilesSelected(files)
  }

  // Implicit label->input nesting only - no `htmlFor`. A label that both wraps
  // AND points at the same input via `htmlFor` fires the file dialog twice and
  // the browser cancels the duplicate, so the picker never opens (the UID-60
  // "Upload a CSV does nothing" bug `CsvImportControl.tsx` documents); nesting
  // alone is one clean trigger.
  const label = (
    <label
      className={dragOver ? 'uploads-dropzone uploads-dropzone-drag-over' : 'uploads-dropzone'}
      onDragOver={(event) => {
        event.preventDefault()
        if (disabled) return
        // Explicitly mark this a copy target. Several browsers will NOT fire
        // `onDrop` unless a `dropEffect` is set during drag-over — the symptom is
        // "drag-and-drop does nothing" while the click-to-browse path still works.
        // `dataTransfer` is guarded because jsdom drag events (unit tests) omit it.
        if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy'
        setDragOver(true)
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
    >
      <input
        type="file"
        multiple
        accept={CAD_ACCEPT_ATTR}
        className="sr-only"
        disabled={disabled}
        onChange={(event) => {
          if (event.target.files) onFilesSelected([...event.target.files])
          event.target.value = ''
        }}
      />
      {/* Design's fanned 3-card upload illustration (`Fictiv Platform.dc.html`
          .fx-fan): a lined "paper" card, a folder card, and a center card
          showing a CAD-part render — the cards spread apart on hover/drag. The
          upload circle is not in the fan; it sits inline before the title. */}
      <div className="uploads-dropzone-fan" aria-hidden="true">
        <div className="uploads-dropzone-fan-doc uploads-dropzone-fan-l">
          <span className="uploads-dropzone-fan-ear" />
          <svg
            viewBox="0 0 60 76"
            style={{ width: '100%', height: '100%', padding: '9px 8px' }}
            fill="none"
            stroke="#c6c6cc"
            strokeWidth="1.5"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <line x1="8" y1="17" x2="46" y2="17" />
            <line x1="8" y1="23" x2="38" y2="23" />
            <path d="M12 35 q11 -9 22 0" />
            <line x1="8" y1="47" x2="46" y2="47" />
            <line x1="8" y1="53" x2="38" y2="53" />
            <path d="M12 65 q11 -9 22 0" />
          </svg>
        </div>
        <div className="uploads-dropzone-fan-doc uploads-dropzone-fan-r">
          <span className="uploads-dropzone-fan-ear" />
          <svg viewBox="0 0 60 76" style={{ width: 38, height: 38 }} fill="none" aria-hidden="true">
            <path
              d="M9 24 h13 l4 5 h25 a3 3 0 0 1 3 3 v20 a3 3 0 0 1 -3 3 H9 a3 3 0 0 1 -3 -3 V27 a3 3 0 0 1 3 -3 z"
              fill="#8c8c92"
            />
            <path d="M30 42 h10 M35 37 v10" stroke="#fff" strokeWidth="3" strokeLinecap="round" />
          </svg>
        </div>
        <div className="uploads-dropzone-fan-doc uploads-dropzone-fan-c">
          <span className="uploads-dropzone-fan-ear" />
          <img src={cadPartRenderUrl} alt="" className="uploads-dropzone-fan-part" />
        </div>
      </div>
      <div className="uploads-dropzone-text">
        <div className="uploads-dropzone-heading-row">
          <span className="uploads-dropzone-icon-circle" aria-hidden="true">
            <IconUpload size={17} />
          </span>
          <p className="uploads-dropzone-title">{title}</p>
        </div>
        <p className="caption-text" style={{ margin: 'var(--space-1) 0 0' }}>
          {helperText}
        </p>
      </div>
    </label>
  )

  if (bare) return label

  return (
    <div className="card" style={{ padding: 0 }}>
      {label}
    </div>
  )
}
